import React, { useEffect, useState, useCallback } from 'react'
import { BlobProvider } from '@react-pdf/renderer'
import Spread from './PDF/Spread'
import axios from 'axios'
import { getSkuLocation } from 'src/services'
import * as XLSX from 'xlsx'

function SpreadReportsOpen() {
  const [dateRange, setDateRange] = useState(null)
  const [error, setError] = useState('')
  const [loadingDate, setLoadingDate] = useState(true)
  const [pdfData, setPDFDate] = useState(null)
  const [map_data, setMapData] = useState({})
  const [additionalLineItems, setAdditionalLineItems] = useState([])

  const formatDate = (date) => {
    const d = new Date(date)
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const year = d.getFullYear()
    return [year, month, day].join('-')
  }

  const formatDateRange = (range) => {
    if (!range || !Array.isArray(range)) return ''
    const [start, end] = range
    return `${formatDate(start)} to ${formatDate(end)}`
  }

  const getDataReport = useCallback((range) => {
    setLoadingDate(true)

    axios
      .post(`/api/get_custom_report`, { data: range })
      .then((res) => {
        console.log('get_custom_report response:', res.data)

        if (!res.data.spreadreportopen || Object.keys(res.data.spreadreportopen).length === 0) {
          setError('No data found')
          setPDFDate(null)
          setAdditionalLineItems([])
        } else {
          setError(null)
          setMapData({
            dzone: res.data.dzone,
            otype: res.data.otype,
            machine: res.data.machine,
            product: res.data.product,
            order_list_products: res.data.order_list_products,
            sku_location:
              res.data.sku_location || res.data.skulocation || res.data.skuLocation || [],
          })
          setPDFDate(res.data.spreadreportopen)

          const orderIds = new Set()
          const spread = res.data.spreadreportopen || {}

          Object.keys(spread).forEach((zoneId) => {
            Object.keys(spread[zoneId] || {}).forEach((prodId) => {
              ;(spread[zoneId][prodId] || []).forEach((row) => {
                if (row && row.id) orderIds.add(Number(row.id))
              })
            })
          })

          let collectedLineItems = []

          if (res.data.orders) {
            const traverse = (node) => {
              if (!node) return

              if (Array.isArray(node)) {
                node.forEach((o) => {
                  if (!o || !o.id) return

                  const items = Array.isArray(o.other_line_items)
                    ? o.other_line_items
                    : Array.isArray(o.line_items)
                    ? o.line_items
                    : null

                  if (items && orderIds.has(Number(o.id))) {
                    collectedLineItems.push(...items)
                  }
                })
              } else if (typeof node === 'object') {
                Object.values(node).forEach((v) => traverse(v))
              }
            }

            traverse(res.data.orders)
          }

          setAdditionalLineItems(collectedLineItems)
        }
      })
      .catch((err) => {
        setError('Error fetching report data')
        setAdditionalLineItems([])
        console.error(err)
      })
      .finally(() => setLoadingDate(false))
  }, [])

  useEffect(() => {
    const today = new Date()
    const lastWeek = new Date()
    lastWeek.setFullYear(today.getFullYear() - 2)

    const defaultRange = [lastWeek, today]
    setDateRange(defaultRange)
    getDataReport(defaultRange)
  }, [getDataReport])

  const findProduct = (id) => map_data.product?.find((x) => x.id === Number(id))

  const findMix = (orderId, productId) => {
    return (
      map_data.order_list_products?.find(
        (x) => x.order_id === Number(orderId) && x.product_mix_id === findProduct(productId)?.id,
      ) || null
    )
  }

  const computeRowTonnes = (dtRow, product) => {
    const found = findMix(dtRow.id, product)
    const mixPercentage = found ? found.mix_percentage : 0
    return (Number(mixPercentage) / 100) * Number(dtRow.tons_ordered || 0)
  }

  const computeRowTonnesActual = (dtRow, product) => {
    const found = findMix(dtRow.id, product)
    const mixPercentage = found ? found.mix_percentage : 0
    return (Number(mixPercentage) / 100) * Number(dtRow.tons_actual || 0)
  }

  const computeRowCompleted = (dtRow, product) => {
    const found = findMix(dtRow.id, product)
    const mixPercentage = found ? found.mix_percentage : 0
    if (dtRow.order_status === 1) {
      return (Number(mixPercentage) / 100) * Number(dtRow.tons_actual || 0)
    }
    return (Number(mixPercentage) / 100) * Number(dtRow.tons_ordered || 0)
  }

  const downloadExcel = async () => {
    if (!pdfData) return

    let skuList = map_data.sku_location || map_data.skulocation || map_data.skuLocation || []

    if (!Array.isArray(skuList) || skuList.length === 0) {
      try {
        const resp = await getSkuLocation()
        skuList =
          resp?.data?.sku_location ||
          resp?.data?.skulocation ||
          resp?.data?.skuLocation ||
          resp?.data ||
          []

        setMapData((prev) => ({
          ...prev,
          sku_location: skuList,
        }))
      } catch (e) {
        console.warn('getSkuLocation() failed', e)
        skuList = []
      }
    }

    const headers = [
      'Zone',
      'Product',
      'Customer Name',
      'Spread Date',
      'Order Date',
      'Tonnes Ordered',
      'Tonnes Actual',
      'Predicted',
    ]

    const aoa = []
    aoa.push(headers)

    let grandOrdered = 0
    let grandActual = 0
    let grandPred = 0

    const subtotalRows = []
    const zoneHeaderRows = []
    let currentRow = 1

    Object.keys(pdfData).forEach((zoneId) => {
      const zoneName = map_data.dzone?.find((z) => z.id === Number(zoneId))?.name || zoneId

      aoa.push([zoneName, '', '', '', '', '', '', ''])
      zoneHeaderRows.push(currentRow)
      currentRow += 1

      Object.keys(pdfData[zoneId] || {}).forEach((productId) => {
        const productObj = findProduct(productId)
        const productLabel =
          productObj?.product_docket || productObj?.product_name || `Product ${productId}`
        const list = pdfData[zoneId][productId] || []

        let subtotalOrdered = 0
        let subtotalActual = 0
        let subtotalPred = 0

        list.forEach((dtRow) => {
          const ordered = computeRowTonnes(dtRow, productId)
          const actual = computeRowTonnesActual(dtRow, productId)
          const pred = computeRowCompleted(dtRow, productId)

          subtotalOrdered += ordered
          subtotalActual += actual
          subtotalPred += pred

          grandOrdered += ordered
          grandActual += actual
          grandPred += pred

          aoa.push([
            '',
            productLabel,
            dtRow.customer?.account_name || '',
            dtRow.tons_complete_time || '',
            formatDate(dtRow.created_at),
            Number(Number(ordered).toFixed(3)),
            Number(Number(actual).toFixed(3)),
            Number(Number(pred).toFixed(3)),
          ])
          currentRow += 1
        })

        const subtotalIndex = currentRow
        aoa.push([
          '',
          productLabel + ' Subtotal',
          '',
          '',
          '',
          Number(Number(subtotalOrdered).toFixed(3)),
          Number(Number(subtotalActual).toFixed(3)),
          Number(Number(subtotalPred).toFixed(3)),
        ])
        subtotalRows.push(subtotalIndex)
        currentRow += 1
      })
    })

    aoa.push(['', '', '', '', '', '', '', ''])
    const grandIndex = aoa.length
    aoa.push([
      '',
      'GRAND TOTAL',
      '',
      '',
      '',
      Number(Number(grandOrdered).toFixed(3)),
      Number(Number(grandActual).toFixed(3)),
      Number(Number(grandPred).toFixed(3)),
    ])

    const ws = XLSX.utils.aoa_to_sheet(aoa)

    ws['!cols'] = [
      { wpx: 150 },
      { wpx: 200 },
      { wpx: 300 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
      { wpx: 120 },
    ]

    headers.forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c })
      if (!ws[addr]) ws[addr] = { t: 's', v: headers[c] }
      ws[addr].s = { font: { bold: true } }
    })

    ws['!merges'] = zoneHeaderRows.map((r) => ({
      s: { r, c: 0 },
      e: { r, c: headers.length - 1 },
    }))

    zoneHeaderRows.forEach((r) => {
      const addr = XLSX.utils.encode_cell({ r, c: 0 })
      if (!ws[addr]) ws[addr] = { t: 's', v: '' }
      ws[addr].s = { font: { bold: true } }
    })

    subtotalRows.forEach((r) => {
      for (let c = 0; c < headers.length; c++) {
        const addr = XLSX.utils.encode_cell({ r, c })
        if (ws[addr]) {
          ws[addr].s = { font: { bold: true }, alignment: { horizontal: 'right' } }
        }
      }
    })

    for (let c = 0; c < headers.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: grandIndex, c })
      if (ws[addr]) {
        ws[addr].s = { font: { bold: true }, alignment: { horizontal: 'right' } }
      }
    }

    const numericCols = [5, 6, 7]
    for (let r = 1; r <= aoa.length - 1; r++) {
      numericCols.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r, c })
        if (ws[addr]) {
          ws[addr].t = 'n'
          ws[addr].s = Object.assign({}, ws[addr].s || {}, {
            alignment: { horizontal: 'right' },
          })
        }
      })
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Spread Report')

    // Build additional line items fresh at download time
    const combinedLineItems = []

    if (Array.isArray(additionalLineItems) && additionalLineItems.length > 0) {
      combinedLineItems.push(...additionalLineItems)
    }

    Object.keys(pdfData).forEach((zoneId) => {
      Object.keys(pdfData[zoneId] || {}).forEach((productId) => {
        ;(pdfData[zoneId][productId] || []).forEach((order) => {
          if (!order) return

          const items = Array.isArray(order.other_line_items)
            ? order.other_line_items
            : Array.isArray(order.line_items)
            ? order.line_items
            : []

          items.forEach((it) => {
            if (!it) return
            combinedLineItems.push({
              ...it,
              order_id: it.order_id ?? order.id ?? order.order_id ?? order.order ?? '',
            })
          })
        })
      })
    })

    const seen = new Set()
    const dedupedLineItems = []

    combinedLineItems.forEach((it) => {
      if (!it) return

      const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
      const description = it.description ?? it.desc ?? it.name ?? ''
      const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
      const amount = Number(it.amount ?? it.Amount ?? 0)
      const total = Number(it.total ?? it.Total ?? quantity * amount)

      const key = `${orderId}||${description}||${quantity}||${amount}||${total}`

      if (!seen.has(key)) {
        seen.add(key)
        dedupedLineItems.push({
          ...it,
          order_id: orderId,
          quantity,
          amount,
          total,
        })
      }
    })

    if (dedupedLineItems.length > 0) {
      const headers2 = [
        'Order ID',
        'Line Item Description',
        'Location',
        'Division',
        'Quantity',
        'Amount',
        'Total',
      ]

      const aoa2 = []
      aoa2.push(headers2)

      dedupedLineItems.forEach((it) => {
        const orderId = it.order_id ?? it.orderId ?? it.order ?? ''
        const description = it.description ?? it.desc ?? it.name ?? ''

        let locationVal = it.location ?? it.sku_location ?? it.location_name ?? ''

        if (
          locationVal !== '' &&
          (typeof locationVal === 'number' || /^\d+$/.test(String(locationVal)))
        ) {
          const lid = Number(locationVal)
          const foundLoc = Array.isArray(skuList) ? skuList.find((l) => Number(l.id) === lid) : null
          locationVal = foundLoc?.name || String(lid)
        }

        let divisionVal = it.division ?? it.branch ?? it.division_name ?? ''
        if (
          divisionVal !== '' &&
          (typeof divisionVal === 'number' || /^\d+$/.test(String(divisionVal)))
        ) {
          const did = Number(divisionVal)
          divisionVal =
            did === 1 ? 'Spreading' : did === 2 ? 'Transport' : did === 3 ? 'Mining' : String(did)
        }

        const quantity = Number(it.quantity ?? it.qty ?? it.Qty ?? 0)
        const amount = Number(it.amount ?? it.Amount ?? 0)
        const total = Number(it.total ?? it.Total ?? quantity * amount)

        aoa2.push([orderId, description, locationVal, divisionVal, quantity, amount, total])
      })

      const ws2 = XLSX.utils.aoa_to_sheet(aoa2)

      headers2.forEach((_, c) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (!ws2[addr]) ws2[addr] = { t: 's', v: headers2[c] }
        ws2[addr].s = { font: { bold: true } }
      })

      ws2['!cols'] = [
        { wpx: 100 },
        { wpx: 400 },
        { wpx: 200 },
        { wpx: 150 },
        { wpx: 100 },
        { wpx: 120 },
        { wpx: 140 },
      ]

      XLSX.utils.book_append_sheet(wb, ws2, 'Additional Line Items')
    }

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true })
    const blob = new Blob([wbout], { type: 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `spread_report_${formatDateRange(dateRange)}.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <h1 className="mb-4">Spread Report (Open)</h1>

      {error ? (
        <div className="btn btn-block btn-outline-info btn-sm">{error}</div>
      ) : loadingDate ? (
        <div className="btn btn-block btn-outline-info btn-sm">Loading....</div>
      ) : pdfData ? (
        <>
          <BlobProvider
            document={
              <Spread
                item={pdfData}
                map_data={map_data}
                dateRange={dateRange}
                spread_report_open={true}
              />
            }
          >
            {({ url, loading }) =>
              !loading ? (
                <a
                  download={`spread_report_${formatDateRange(dateRange)}.pdf`}
                  href={url}
                  className="btn btn-block btn-outline-info btn-sm"
                  rel="noreferrer"
                  target="_blank"
                >
                  Download Report
                </a>
              ) : (
                <button type="button" className="btn btn-block btn-outline-info btn-sm" disabled>
                  Preparing Report
                </button>
              )
            }
          </BlobProvider>

          <div style={{ display: 'inline-block', marginLeft: 8 }}>
            <button
              type="button"
              className="btn btn-block btn-outline-success btn-sm"
              onClick={downloadExcel}
            >
              Download Excel
            </button>
          </div>
        </>
      ) : null}
    </>
  )
}

export default SpreadReportsOpen
